
import React, { useMemo, useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import { useAppContext } from '../contexts/AppContext';
import { useTour } from '../contexts/TourContext';
import { FmeaItem } from '../types';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { Map as MapIcon, FolderOpen, Settings, ChevronRight, ChevronDown, Loader2, AlertTriangle, Network, FileText, List, GitBranch, Link, Grid, CheckSquare, ClipboardCheck, FileBarChart, History, Shield, FlaskConical } from 'lucide-react';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { normalizeProjectName, extractProjectName } from '../utils/validation';
import { getStandardConfig, getWorkflowSteps } from '../constants/fmeaStandards';
import { API_BASE_URL } from '../utils/apiConfig';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

const Dashboard: React.FC = () => {
    const { navigate, allData, importProjectData, masterFunctions, functionColorMap, currentUser, activeProject, setActiveProject } = useAppContext();
    const { startTour, isTourActive } = useTour();
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    // Get Standard Configuration for dynamic logic
    const standardConfig = useMemo(() =>
        getStandardConfig(allData.projectMetadata?.fmeaStandard),
        [allData.projectMetadata]
    );

    // Get Workflow Steps for dynamic rendering
    const workflowSteps = useMemo(() =>
        getWorkflowSteps(allData.projectMetadata?.fmeaStandard),
        [allData.projectMetadata]
    );

    // Icon Mapping for dynamic quick links
    const iconMap: Record<string, React.ElementType> = {
        'Layout': Grid,
        'FileText': FileText,
        'AlertTriangle': AlertTriangle,
        'Network': Network,
        'List': List,
        'GitBranch': GitBranch,
        'Link': Link,
        'CheckSquare': CheckSquare,
        'ClipboardCheck': ClipboardCheck,
        'FileBarChart': FileBarChart,
        'History': History,
        'Shield': Shield,
        'FlaskConical': FlaskConical
    };

    // Quick Project Selector state
    const [projectList, setProjectList] = useState<any[]>([]);
    const [isLoadingProject, setIsLoadingProject] = useState(false);
    const [loadSuccess, setLoadSuccess] = useState(false);
    const [expandedStep, setExpandedStep] = useState<number | null>(null);

    // Fetch available projects on mount - Using exact logic from ProjectManagement.tsx
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                // Use authenticated fetch for project list
                const data = await authenticatedFetchJSON('/api/projects/');

                if (Array.isArray(data)) {
                    setProjectList(data);
                } else if (data?.value && Array.isArray(data.value)) {
                    // Fallback for OData/PowerShell formatted responses just in case
                    setProjectList(data.value);
                }
            } catch (error) {
                console.error('Failed to fetch project list:', error);
            }
        };
        fetchProjects();
    }, []);

    // Quick load project handler
    const handleQuickLoadProject = async (projectName: string) => {
        if (!projectName) return;
        setIsLoadingProject(true);
        setLoadSuccess(false);
        try {
            const projectData = await authenticatedFetchJSON(`/api/projects/load/${encodeURIComponent(projectName)}`);

            // Success if no error thrown
            await importProjectData(projectData, true);
            setActiveProject(projectData.projectMetadata?.name || projectName);
            setLoadSuccess(true);
            setTimeout(() => setLoadSuccess(false), 2000);
        } catch (error) {
            console.error('Failed to load project:', error);
            alert('Failed to load project. Please try from Project Management page.');
        } finally {
            setIsLoadingProject(false);
        }
    };

    // Show modal when Dashboard mounts
    useEffect(() => {
        if (currentUser && currentUser.role !== 'admin' && currentUser.role !== 'magnus') {
            const timer = setTimeout(() => {
                // Only show if tour hasn't started in the meantime
                if (!isTourActive) {
                    setShowWelcomeModal(true);
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [currentUser?.username, isTourActive]); // Trigger when user changes or tour status changes

    const handleStartWelcomeTour = () => {
        setShowWelcomeModal(false);
        startTour();
    };

    const handleDismissWelcome = () => {
        setShowWelcomeModal(false);
    };

    // Helper to check if an item belongs to a project
    const isItemInProject = (item: FmeaItem) => {
        if (activeProject === 'all') return true;
        const activeProjectNormalized = normalizeProjectName(activeProject);

        // 1. Direct Project check (Metadata Standards v2.0.0)
        if (item.projectName) {
            return normalizeProjectName(item.projectName) === activeProjectNormalized;
        }

        // 2. Fallback: Find the epoch this item belongs to
        const epoch = allData.iterationHistory?.find(e =>
            (e.epochNumber || e.iteration) === item.epochId
        );

        if (!epoch) return false;

        const projectName = extractProjectName(epoch.epochName || '');
        return normalizeProjectName(projectName) === activeProjectNormalized;
    };

    // --- DATA PROCESSING ---
    const highRiskItems = useMemo(() => {
        // Safety check: ensure fmeaItems is an array
        if (!allData.fmeaItems || !Array.isArray(allData.fmeaItems)) {
            return [];
        }

        // Filter by project first
        const projectItems = allData.fmeaItems.filter(item => isItemInProject(item));
        const riskCalc = standardConfig.riskMatrix.getRiskLevel;

        // Calculate risk index for all items and sort
        // Risk Index: 0 = Highest Risk (Critical/Catastrophic/ASIL D)
        const scoredItems = projectItems.map(item => ({
            item,
            riskIndex: riskCalc(item.S, item.O, item.D),
            // Tie-breaker: Raw RPN or S
            rawScore: (item.S * item.O * item.D)
        }));

        // Sort: Lower index is worse (0=Critical). If tie, higher raw score (RPN/S) is worse.
        scoredItems.sort((a, b) => {
            if (a.riskIndex !== b.riskIndex) return a.riskIndex - b.riskIndex; // 0 before 1
            return b.rawScore - a.rawScore; // Higher RPN first within same band
        });

        return scoredItems.slice(0, 5).map(x => x.item);
    }, [allData, activeProject, standardConfig]);

    const metricName = useMemo(() => {
        if (standardConfig.riskModel === 'RPN') return 'RPN';
        return standardConfig.columns.find(c => c.key === 'actionPriority')?.header || 'Priority';
    }, [standardConfig]);

    const riskStats = useMemo(() => {
        // Safety check: ensure fmeaItems exists and is an array
        if (!allData.fmeaItems || !Array.isArray(allData.fmeaItems)) {
            return [];
        }

        const projectItems = allData.fmeaItems.filter(f => isItemInProject(f));
        const usedFunctions = Array.from(new Set(projectItems.map(f => f.function))).filter(Boolean);
        const functionsToMap = usedFunctions.length > 0 ? usedFunctions : masterFunctions;

        const palette = [
            '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
            '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#6366f1'
        ];

        const getColorForString = (str: string) => {
            const hash = str.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
            return palette[hash % palette.length];
        };

        const stats = functionsToMap.map(fn => {
            const items = projectItems.filter(f => f.function === fn);

            // Use standard-specific metric for accumulation
            const metricKey = standardConfig.riskModel === 'RPN' ? 'RPN' : 'actionPriority';
            const totalMetric = items.reduce((acc: number, curr: any) => {
                const val = curr[metricKey];
                if (typeof val === 'number') return acc + val;
                // AIAG-VDA AP weights
                if (val === 'H') return acc + 100; // Weighted for visibility
                if (val === 'M') return acc + 50;
                if (val === 'L') return acc + 10;
                return acc + (curr.RPN || 0);
            }, 0);

            const color = functionColorMap[fn] || getColorForString(fn);

            return { fn, totalMetric, color };
        });

        return stats.sort((a, b) => b.totalMetric - a.totalMetric);
    }, [allData.fmeaItems, masterFunctions, functionColorMap, activeProject, standardConfig]);

    const maxMetric = Math.max(...riskStats.map(s => s.totalMetric), 1);

    const handleItemClick = (item: FmeaItem) => {
        navigate('/fmeas', { state: { type: 'fmea', id: item.id } });
    };

    return (
        <div className="space-y-6">
            {/* Quick Project Selector Card */}
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 border border-primary/20 rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-xl shadow-lg shrink-0">
                            <FolderOpen size={20} className="text-white" />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full">
                            <div>
                                <h2 className="text-lg font-bold text-text-primary whitespace-nowrap">Quick Project Load</h2>
                                <p className="text-xs text-text-secondary">Select to load instantly</p>
                            </div>

                            <select
                                className="px-4 py-2.5 h-11 bg-white border-2 border-primary/40 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary shadow-md w-full sm:max-w-[400px] cursor-pointer hover:border-primary transition-all text-ellipsis"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleQuickLoadProject(e.target.value);
                                        // Reset selection so user can re-select same project if needed (though unlikely)
                                        e.target.value = "";
                                    }
                                }}
                                defaultValue=""
                                disabled={isLoadingProject}
                            >
                                <option value="">
                                    {isLoadingProject ? "Loading..." : "📁 Select a project to load..."}
                                </option>
                                {projectList.map(p => (
                                    <option key={p.name} value={p.name}>
                                        {p.displayName || p.name} ({p.fmeaStandard ? p.fmeaStandard.replace(/_/g, ' ') : 'AIAG VDA'})
                                    </option>
                                ))}
                            </select>

                            {/* Loading / Success Indicator */}
                            {isLoadingProject && (
                                <div className="flex items-center gap-2 text-primary text-sm font-medium animate-pulse">
                                    <Loader2 size={16} className="animate-spin" />
                                    Loading...
                                </div>
                            )}
                            {loadSuccess && (
                                <div className="flex items-center gap-2 text-green-600 text-sm font-bold animate-fade-in">
                                    <FolderOpen size={16} />
                                    Loaded!
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/project-management')}
                            className="flex items-center gap-1.5 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors border border-gray-200 whitespace-nowrap"
                            title="Open Project Management"
                        >
                            <Settings size={14} />
                            Manage
                            <ChevronRight size={14} />
                        </button>
                    </div>
                </div>

                {/* Current Project Badge */}
                {activeProject && activeProject !== 'all' && (
                    <div className="mt-3 pt-3 border-t border-primary/10 flex items-center gap-2">
                        <span className="text-xs text-text-secondary">Currently loaded:</span>
                        <ActiveProjectBadge />
                    </div>
                )}
            </div>

            <div className="pb-4 border-b-4 border-primary flex justify-between items-start">
                <div>
                    {/* ... */}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => startTour()}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg transition-all shadow-md hover:shadow-lg text-sm font-bold"
                    >
                        <MapIcon size={16} /> Start Tour
                    </button>
                    <ModuleInfoModal
                        title="DfR Dashboard"
                        description="The central hub for monitoring the Design for Reliability (DfR) process. It provides a high-level overview of project health, critical risks, and system status."
                        benefits={[
                            `Instant visibility into top-priority risks (High ${standardConfig.riskModel === 'RPN' ? 'RPN' : 'Risk'} items).`,
                            "Track risk distribution across different system functions.",
                            "Filter data by specific project for focused analysis.",
                            "Understand the flow of the DfR process from definition to validation."
                        ]}
                        usageInstructions={[
                            "Use the 'Quick Project Load' section to load a project instantly.",
                            "Review the 'Top 5 High-Priority Risks' table to see what needs immediate action.",
                            "Use the 'Risk Distribution' chart to identify which subsystems carry the most risk.",
                            "Click 'View' on any risk item to jump directly to its FMEA entry."
                        ]}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <p className="text-text-secondary leading-relaxed">
                            This project is governed by the <strong>{standardConfig.name}</strong> standard and organized around <strong>{masterFunctions.length || 12}</strong> System Functions. Use the left navigation to explore P‑Diagrams, FMEAs, Risk Matrices, Traceability, and the DVP&R.
                        </p>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm items-start">
                            {workflowSteps.map((step, idx) => {
                                const isExpanded = expandedStep === idx;
                                return (
                                    <div
                                        key={idx}
                                        className={`bg-background p-3 rounded border transition-all cursor-pointer relative overflow-hidden ${isExpanded ? 'border-primary ring-1 ring-primary shadow-lg z-10' : 'border-border hover:border-primary/50'}`}
                                        onClick={() => setExpandedStep(isExpanded ? null : idx)}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="font-bold text-primary block pr-2 leading-tight">{step.title}</span>
                                            {step.quickLinks && step.quickLinks.length > 0 && (
                                                <ChevronDown className={`w-4 h-4 text-text-muted shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                            )}
                                        </div>
                                        <span className="text-text-secondary block mb-3 text-xs leading-relaxed">{step.description}</span>

                                        {/* Quick Links Section */}
                                        {isExpanded && step.quickLinks && (
                                            <div className="mt-3 pt-3 border-t border-border space-y-2 animate-in fade-in slide-in-from-top-1">
                                                <div className="flex items-center gap-1.5 mb-2">
                                                    <span className="text-[10px] uppercase font-bold text-text-muted tracking-wider">Quick Actions</span>
                                                </div>
                                                {step.quickLinks.map((link, linkIdx) => {
                                                    const IconComponent = link.icon ? iconMap[link.icon] : ChevronRight;
                                                    return (
                                                        <button
                                                            key={linkIdx}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(link.path);
                                                            }}
                                                            className="w-full text-left flex items-start gap-2.5 p-2 hover:bg-surface-hover rounded-md text-xs group transition-all border border-transparent hover:border-border/50"
                                                            title={link.description}
                                                        >
                                                            <div className="p-1.5 bg-surface border border-border rounded-md group-hover:border-primary/30 group-hover:bg-primary/5 group-hover:text-primary transition-colors shrink-0 mt-0.5">
                                                                {IconComponent ? <IconComponent className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                                            </div>
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="font-semibold text-text-primary group-hover:text-primary transition-colors truncate w-full">{link.label}</span>
                                                                <span className="text-[10px] text-text-muted line-clamp-2 leading-snug">{link.description}</span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {!isExpanded && step.quickLinks && step.quickLinks.length > 0 && (
                                            <div className="mt-auto pt-2 flex items-center gap-2 text-[10px] text-text-muted/60 font-medium">
                                                <div className="flex -space-x-1">
                                                    {step.quickLinks.slice(0, 3).map((l, i) => (
                                                        <div key={i} className="w-1.5 h-1.5 rounded-full bg-primary/20 ring-1 ring-background" />
                                                    ))}
                                                </div>
                                                <span>{step.quickLinks.length} Actions Available</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    <Card title="Top 5 High-Priority Risks">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-text-secondary">
                                <thead className="text-xs text-text-primary uppercase bg-background/50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3">Risk Item</th>
                                        <th scope="col" className="px-4 py-3 text-right">
                                            {standardConfig.riskModel === 'RPN' ? 'RPN' : (
                                                standardConfig.columns.find(c => c.key === 'actionPriority')?.header || 'Risk Level'
                                            )}
                                        </th>
                                        <th scope="col" className="px-4 py-3">Function</th>
                                        <th scope="col" className="px-4 py-3">Target Requirement</th>
                                        <th scope="col" className="px-4 py-3 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {highRiskItems.map((item, index) => {
                                        const riskIndex = standardConfig.riskMatrix.getRiskLevel(item.S, item.O, item.D);
                                        const riskLevel = standardConfig.riskMatrix.levels[riskIndex];

                                        return (
                                            <tr key={item.id} className="border-b border-border hover:bg-background group">
                                                <td className="px-4 py-3 font-medium text-text-primary">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${riskLevel ? riskLevel.bgClass + ' ' + riskLevel.textClass : 'bg-gray-100 text-gray-600'}`}>
                                                            {index + 1}
                                                        </span>
                                                        {item.failureMode}
                                                    </div>
                                                </td>
                                                <td className={`px-4 py-3 font-bold text-right ${riskLevel?.textClass || ''}`}>
                                                    {standardConfig.riskModel === 'RPN' ? item.RPN : riskLevel?.name || 'High'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="px-2 py-1 text-xs rounded whitespace-nowrap opacity-75" style={{ backgroundColor: `${functionColorMap[item.function]}20` }}>
                                                        {item.function.length > 20 ? item.function.substring(0, 18) + '...' : item.function}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-xs text-text-secondary line-clamp-2" title={item.requirement}>
                                                        {item.requirement || 'Not specified'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button onClick={() => handleItemClick(item)} className="font-medium text-primary hover:underline text-xs">
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>

                <div className="lg:col-span-1">
                    <Card title="Risk Distribution by Function">
                        <div className="space-y-4">
                            <p className="text-xs text-text-secondary mb-2">Total Accumulated {metricName} per System Function.</p>
                            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                                {riskStats.map((stat) => (
                                    <div key={stat.fn} className="group">
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className="font-medium text-text-primary truncate w-3/4" title={stat.fn}>{stat.fn}</span>
                                            <span className="text-text-secondary font-mono">
                                                {typeof stat.totalMetric === 'number' && stat.totalMetric > 1000 ?
                                                    stat.totalMetric.toExponential(1) :
                                                    stat.totalMetric
                                                }
                                            </span>
                                        </div>
                                        <div className="w-full bg-background rounded-full h-2.5 overflow-hidden border border-border/50">
                                            <div
                                                className="h-full rounded-full transition-all duration-1000 ease-out group-hover:brightness-110"
                                                style={{
                                                    width: `${(stat.totalMetric / maxMetric) * 100}%`,
                                                    backgroundColor: stat.color
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Welcome Modal for Non-Admins */}
            {showWelcomeModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 max-w-md w-full border border-purple-100 dark:border-purple-900 mx-4 relative overflow-hidden">
                        {/* Decorative Background Blob */}
                        <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl"></div>

                        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                            Welcome to DfR Platform! 🚀
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                            Getting started? We&apos;ve prepared a quick <strong>System Tour</strong> to show you the powerful AI features, FMEA tools, and Reliability Suite.
                        </p>

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={handleStartWelcomeTour}
                                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-bold font-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                            >
                                <MapIcon size={20} />
                                See System Tour
                            </button>
                            <button
                                onClick={handleDismissWelcome}
                                className="w-full py-3 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-xl font-medium transition-colors"
                            >
                                No thanks, get going
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
